/**
 * FleetProgressionSystem.js
 *
 * Manages tiered aircraft access in Simulation mode.
 * Players start with regional jets and unlock larger aircraft
 * by expanding their network, reputation, or route length.
 *
 * Tiers:
 * 1. Regional (start) - E175, CRJ, ATR, Dash 8
 * 2. Narrowbody - 737, A320 family (unlock via major slots, 1500nm route, or 7000 rep)
 * 3. Widebody - 787, A350, 777 (unlock via international slots, 3500nm route, or 12000 rep)
 * 4. Heavy - A380, 747 (unlock via 15000 rep, 3+ international airports, or 50 widebody flights)
 */

import { debug } from '../lib/debug.js';

// Major US airports that trigger narrowbody unlock
// NOTE: KSEA, KBOI, KRDM excluded - players start with slots there
export const MAJOR_AIRPORTS = [
  'KDEN', 'KSFO', 'KLAX', 'KJFK', 'KORD', 'KATL',
  'KDFW', 'KMIA', 'KBOS', 'KPHX', 'KLAS', 'KMSP',
  'KIAH', 'KEWR', 'KMCO', 'KPHL', 'KCLT',
  'KDCA', 'KSAN', 'KTPA', 'KSTL', 'KBWI', 'KAUS',
];

// International airports that trigger widebody unlock
export const INTERNATIONAL_AIRPORTS = [
  // Europe
  'EGLL', 'LFPG', 'EDDF', 'EHAM', 'LEMD', 'LIRF',
  'EGKK', 'LFPO', 'EDDM', 'LEBL', 'LSZH', 'LOWW',
  'EKCH', 'ENGM', 'ESSA', 'EFHK', 'LPPT', 'EIDW',
  // Asia Pacific
  'RJTT', 'RJAA', 'VHHH', 'WSSS', 'RKSI', 'VTBS',
  'ZBAA', 'ZSPD', 'ZGGG', 'RPLL', 'WIII', 'VIDP',
  // Middle East
  'OMDB', 'OERK', 'OTHH', 'OBBI', 'OLBA',
  // Canada
  'CYYZ', 'CYVR', 'CYUL', 'CYYC',
  // Central/South America
  'MMMX', 'MMUN', 'SBGR', 'SCEL', 'SKBO', 'SPJC',
  // Africa
  'FAOR', 'HECA', 'GMMN', 'DNMM',
  // Oceania
  'YSSY', 'YMML', 'NZAA', 'NZWN',
];

// Default unlock state for new games
export const DEFAULT_FLEET_UNLOCKS = {
  regional: true,      // Always unlocked
  narrowbody: false,
  widebody: false,
  heavy: false,
};

// Reputation thresholds
export const REP_THRESHOLDS = {
  narrowbody: 7000,    // Tier 3 rep
  widebody: 12000,     // Tier 4 rep
  heavy: 15000,        // Tier 5 rep
};

// Route distance thresholds (nautical miles)
export const ROUTE_THRESHOLDS = {
  narrowbody: 1500,
  widebody: 3500,
};

/**
 * Check if an airport is a major US hub
 */
export function isMajorAirport(icao) {
  return MAJOR_AIRPORTS.includes(icao);
}

/**
 * Check if an airport is international (non-US)
 */
export function isInternationalAirport(icao) {
  return INTERNATIONAL_AIRPORTS.includes(icao);
}

/**
 * Check all unlock conditions and return updated unlocks + any new unlocks
 * @param {Object} worldState - Current world state
 * @returns {{ unlocks: Object, newUnlocks: string[] }} Updated unlocks and list of newly unlocked tiers
 */
export function checkFleetUnlocks(worldState) {
  const { slots, routes, stats, reputation, fleetUnlocks } = worldState;

  // Get current unlocks or default (fleetUnlocks is at worldState top level, not inside airline)
  const currentUnlocks = { ...DEFAULT_FLEET_UNLOCKS, ...(fleetUnlocks || {}) };
  const newUnlocks = [];

  // Get reputation score
  const repScore = reputation?.currentScore || 0;

  // Get all airports with slots
  const slottedAirports = Object.keys(slots || {}).filter(apt =>
    Array.isArray(slots[apt]) && slots[apt].length > 0
  );

  // Count international airports with slots
  const internationalSlotCount = slottedAirports.filter(apt =>
    isInternationalAirport(apt)
  ).length;

  // Find longest route
  const longestRoute = (routes || []).reduce((max, route) => {
    const dist = route.distanceNm || route.distance || 0;
    return dist > max ? dist : max;
  }, 0);

  // Get widebody flight count
  const widebodyFlights = stats?.widebodyFlights || 0;

  // ===== NARROWBODY UNLOCK =====
  if (!currentUnlocks.narrowbody) {
    const hasMajorSlots = slottedAirports.some(apt => isMajorAirport(apt));
    const hasLongRoute = longestRoute > ROUTE_THRESHOLDS.narrowbody;
    const hasRep = repScore >= REP_THRESHOLDS.narrowbody;

    if (hasMajorSlots || hasLongRoute || hasRep) {
      currentUnlocks.narrowbody = true;
      newUnlocks.push('narrowbody');

      let reason = '';
      if (hasMajorSlots) reason = 'Slots at major airport';
      else if (hasLongRoute) reason = `Route over ${ROUTE_THRESHOLDS.narrowbody}nm`;
      else if (hasRep) reason = `Reputation ${REP_THRESHOLDS.narrowbody}+`;

      debug(`[FleetProgression] Narrowbody unlocked: ${reason}`);
    }
  }

  // ===== WIDEBODY UNLOCK =====
  if (!currentUnlocks.widebody) {
    const hasInternationalSlots = slottedAirports.some(apt => isInternationalAirport(apt));
    const hasVeryLongRoute = longestRoute > ROUTE_THRESHOLDS.widebody;
    const hasRep = repScore >= REP_THRESHOLDS.widebody;

    if (hasInternationalSlots || hasVeryLongRoute || hasRep) {
      currentUnlocks.widebody = true;
      newUnlocks.push('widebody');

      let reason = '';
      if (hasInternationalSlots) reason = 'International slots acquired';
      else if (hasVeryLongRoute) reason = `Route over ${ROUTE_THRESHOLDS.widebody}nm`;
      else if (hasRep) reason = `Reputation ${REP_THRESHOLDS.widebody}+`;

      debug(`[FleetProgression] Widebody unlocked: ${reason}`);
    }
  }

  // ===== HEAVY UNLOCK =====
  if (!currentUnlocks.heavy) {
    const hasRep = repScore >= REP_THRESHOLDS.heavy;
    const hasMultipleInternational = internationalSlotCount >= 3;
    const hasManyWidebodyFlights = widebodyFlights >= 50;

    if (hasRep || hasMultipleInternational || hasManyWidebodyFlights) {
      currentUnlocks.heavy = true;
      newUnlocks.push('heavy');

      let reason = '';
      if (hasRep) reason = `Reputation ${REP_THRESHOLDS.heavy}+`;
      else if (hasMultipleInternational) reason = `Slots at ${internationalSlotCount} international airports`;
      else if (hasManyWidebodyFlights) reason = `${widebodyFlights} widebody flights completed`;

      debug(`[FleetProgression] Heavy unlocked: ${reason}`);
    }
  }

  return { unlocks: currentUnlocks, newUnlocks };
}

/**
 * Filter aircraft list based on current unlock state
 * @param {Array} allAircraft - All aircraft from database
 * @param {Object} worldState - Current world state
 * @returns {Array} Filtered aircraft list with locked status
 */
export function filterAircraftByUnlocks(allAircraft, worldState) {
  const mode = worldState?.airline?.mode || worldState?.gameMode;

  // Simple mode: everything available
  if (mode === 'simple') {
    return allAircraft.map(aircraft => ({ ...aircraft, locked: false }));
  }

  // Simulation mode: check unlocks
  const unlocks = {
    ...DEFAULT_FLEET_UNLOCKS,
    ...(worldState?.airline?.fleetUnlocks || {}),
  };

  return allAircraft.map(aircraft => {
    const category = aircraft.category?.toLowerCase() || 'regional';
    let locked = false;
    let lockReason = null;

    switch (category) {
      case 'regional':
        locked = false;
        break;
      case 'narrowbody':
        locked = !unlocks.narrowbody;
        if (locked) lockReason = 'narrowbody';
        break;
      case 'widebody':
        locked = !unlocks.widebody;
        if (locked) lockReason = 'widebody';
        break;
      case 'heavy':
      case 'superheavy':
        locked = !unlocks.heavy;
        if (locked) lockReason = 'heavy';
        break;
      case 'collectible':
        // Collectibles have their own unlock system (EOS)
        locked = false;
        break;
      case 'bizjet':
        // Business jets - treat as narrowbody tier for now
        locked = !unlocks.narrowbody;
        if (locked) lockReason = 'narrowbody';
        break;
      default:
        locked = false;
    }

    return { ...aircraft, locked, lockReason };
  });
}

/**
 * Get unlock requirements for a specific tier
 * @param {string} tier - 'narrowbody', 'widebody', or 'heavy'
 * @returns {Object} Requirements object
 */
export function getUnlockRequirements(tier) {
  switch (tier) {
    case 'narrowbody':
      return {
        tier: 'narrowbody',
        tierName: 'Narrowbody',
        requirements: [
          { type: 'slots', description: 'Win slots at a major airport' },
          { type: 'route', description: `Operate a route over ${ROUTE_THRESHOLDS.narrowbody}nm` },
          { type: 'reputation', description: `Reach ${REP_THRESHOLDS.narrowbody.toLocaleString()} reputation` },
        ],
        repThreshold: REP_THRESHOLDS.narrowbody,
        routeThreshold: ROUTE_THRESHOLDS.narrowbody,
      };
    case 'widebody':
      return {
        tier: 'widebody',
        tierName: 'Widebody',
        requirements: [
          { type: 'slots', description: 'Win international slots' },
          { type: 'route', description: `Operate a route over ${ROUTE_THRESHOLDS.widebody}nm` },
          { type: 'reputation', description: `Reach ${REP_THRESHOLDS.widebody.toLocaleString()} reputation` },
        ],
        repThreshold: REP_THRESHOLDS.widebody,
        routeThreshold: ROUTE_THRESHOLDS.widebody,
      };
    case 'heavy':
      return {
        tier: 'heavy',
        tierName: 'Heavy',
        requirements: [
          { type: 'reputation', description: `Reach ${REP_THRESHOLDS.heavy.toLocaleString()} reputation` },
          { type: 'slots', description: 'Win slots at 3+ international airports' },
          { type: 'flights', description: 'Complete 50+ widebody flights' },
        ],
        repThreshold: REP_THRESHOLDS.heavy,
        widebodyFlightThreshold: 50,
        internationalAirportThreshold: 3,
      };
    default:
      return null;
  }
}

/**
 * Get progress toward a tier unlock
 * @param {string} tier - The tier to check
 * @param {Object} worldState - Current world state
 * @returns {Object} Progress info
 */
export function getUnlockProgress(tier, worldState) {
  const { slots, routes, stats, reputation } = worldState;
  const repScore = reputation?.currentScore || 0;
  const requirements = getUnlockRequirements(tier);

  if (!requirements) return null;

  const slottedAirports = Object.keys(slots || {}).filter(apt =>
    Array.isArray(slots[apt]) && slots[apt].length > 0
  );

  const longestRoute = (routes || []).reduce((max, route) => {
    const dist = route.distanceNm || route.distance || 0;
    return dist > max ? dist : max;
  }, 0);

  const widebodyFlights = stats?.widebodyFlights || 0;
  const internationalSlotCount = slottedAirports.filter(apt =>
    isInternationalAirport(apt)
  ).length;

  switch (tier) {
    case 'narrowbody':
      return {
        reputation: { current: repScore, target: REP_THRESHOLDS.narrowbody },
        route: { current: longestRoute, target: ROUTE_THRESHOLDS.narrowbody },
        hasMajorSlots: slottedAirports.some(apt => isMajorAirport(apt)),
      };
    case 'widebody':
      return {
        reputation: { current: repScore, target: REP_THRESHOLDS.widebody },
        route: { current: longestRoute, target: ROUTE_THRESHOLDS.widebody },
        hasInternationalSlots: slottedAirports.some(apt => isInternationalAirport(apt)),
      };
    case 'heavy':
      return {
        reputation: { current: repScore, target: REP_THRESHOLDS.heavy },
        widebodyFlights: { current: widebodyFlights, target: 50 },
        internationalAirports: { current: internationalSlotCount, target: 3 },
      };
    default:
      return null;
  }
}

/**
 * Get list of aircraft unlocked by a specific tier
 * @param {string} tier - The tier
 * @param {Object} aircraftDatabase - Full aircraft database
 * @returns {Array} List of aircraft models
 */
export function getAircraftForTier(tier, aircraftDatabase) {
  const tierCategories = {
    narrowbody: ['narrowbody'],
    widebody: ['widebody'],
    heavy: ['heavy', 'superheavy'],
  };

  const categories = tierCategories[tier] || [];

  return Object.values(aircraftDatabase).filter(aircraft =>
    categories.includes(aircraft.category?.toLowerCase())
  );
}

/**
 * Get unlock message for a tier
 * @param {string} tier - The tier that was unlocked
 * @returns {Object} Message object with title and description
 */
export function getUnlockMessage(tier) {
  switch (tier) {
    case 'narrowbody':
      return {
        title: 'FLEET EXPANSION AVAILABLE',
        description: 'Your network has grown. Narrowbody aircraft are now available for lease and purchase.',
        aircraftExamples: ['Boeing 737 MAX 8', 'Boeing 737-800', 'Airbus A320neo', 'Airbus A321neo'],
      };
    case 'widebody':
      return {
        title: 'INTERNATIONAL OPERATIONS AUTHORIZED',
        description: 'Widebody aircraft are now available for your growing network.',
        aircraftExamples: ['Boeing 787-9', 'Boeing 777-300ER', 'Airbus A350-900', 'Airbus A330-900neo'],
      };
    case 'heavy':
      return {
        title: 'MAJOR AIRLINE STATUS ACHIEVED',
        description: 'You\'ve built a major airline. All aircraft types are now available.',
        aircraftExamples: ['Airbus A380', 'Boeing 747-8', 'Boeing 747-400'],
      };
    default:
      return null;
  }
}
